/**
 * Engagement detail — the five tabs from the design.
 *
 * Overview, Service Plan, Work & Deliverables, Performance, Time & Billing.
 * The financial figures on the last tab come from a separate endpoint that
 * returns 403 to anyone below manager, so the tab degrades rather than leaking.
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api, type Engagement, type ProfileField, type Cycle,
  type CycleFinancials, type WorkItem, type MetricSnapshot,
} from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, RefreshCw, Settings } from 'lucide-react';
import { ClientProfileCard } from './client-profile-card';
import { AuthorizationsPanel } from './authorizations-panel';
import { EngagementSettingsDialog } from './engagement-settings-dialog';

export function EngagementDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [tab, setTab] = useState('overview');
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'engagement', id],
    queryFn: () => api.getEngagement(id),
    enabled: !!id,
  });
  const { data: cyclesData } = useQuery({
    queryKey: ['svc', 'cycles', id],
    queryFn: () => api.getCycles({ engagementId: id }),
    enabled: !!id,
  });

  const generate = useMutation({
    mutationFn: () => api.generateCycle({ engagementId: id }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not generate the cycle', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: `Cycle ${r.data?.label ?? ''} ready` });
    },
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const e = data?.data as (Engagement & { profile: ProfileField[] }) | undefined;
  if (!e) return <div className="p-6 text-sm text-muted-foreground">Engagement not found.</div>;

  const cycles = (cyclesData?.data ?? []) as Cycle[];
  const current = cycles[0];

  return (
    <div className="p-6 space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/service/engagements')} className="-ml-2">
        <ArrowLeft className="h-4 w-4 mr-1" /> Engagements
      </Button>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-serif font-semibold tracking-tight">{e.account_name}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {e.service_line_name} · #{e.engagement_number} ·{' '}
            {e.monthly_price != null ? `${formatCurrency(e.monthly_price)} / ${e.billing_frequency}` : 'no price set'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4 mr-1.5" /> Settings
          </Button>
          <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Generate cycle
          </Button>
        </div>
      </div>

      <EngagementSettingsDialog
        engagement={e} open={settingsOpen} onOpenChange={setSettingsOpen}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="plan">Service Plan</TabsTrigger>
          <TabsTrigger value="work">Work &amp; Deliverables</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="billing">Time &amp; Billing</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab engagement={e} cycles={cycles} />
        </TabsContent>
        <TabsContent value="plan" className="mt-4">
          <PlanTab planId={e.plan_id} plannedHours={e.planned_hours_per_cycle} />
        </TabsContent>
        <TabsContent value="work" className="mt-4">
          <WorkTab engagementId={e.id} />
        </TabsContent>
        <TabsContent value="performance" className="mt-4">
          <PerformanceTab engagementId={e.id} />
        </TabsContent>
        <TabsContent value="billing" className="mt-4 space-y-4">
          <BillingTab cycle={current} />
          <AuthorizationsPanel engagementId={e.id} cycleId={current?.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ------------------------------------------------------------- tab 1

/**
 * Move a cycle through delivery. The period's work being done and the period
 * being *delivered* are different events — one is a count of tasks, the other
 * is a decision someone makes.
 */
function CycleStatusControl({ cycle }: { cycle: Cycle }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const update = useMutation({
    mutationFn: (status: string) => api.updateCycleStatus(cycle.id, { status }),
    onSuccess: (r) => {
      if (!r.success) {
        toast({ title: 'Could not update', description: r.error?.message, variant: 'destructive' });
        return;
      }
      qc.invalidateQueries({ queryKey: ['svc'] });
      toast({ title: 'Cycle updated' });
    },
  });

  if (cycle.status === 'closed' || cycle.status === 'cancelled') {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const next = cycle.status === 'delivered'
    ? { label: 'Close', value: 'closed' }
    : { label: 'Delivered', value: 'delivered' };

  return (
    <Button
      size="sm" variant="outline"
      onClick={() => update.mutate(next.value)}
      disabled={update.isPending}
    >
      {next.label}
    </Button>
  );
}

function OverviewTab({ engagement, cycles }: { engagement: Engagement & { profile: ProfileField[] }; cycles: Cycle[] }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Engagement</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
            <Row k="Status" v={<Badge variant={engagement.status === 'active' ? 'default' : 'secondary'}>{engagement.status}</Badge>} />
            <Row k="Started" v={formatDate(engagement.start_date)} />
            <Row k="Plan" v={engagement.plan_name ? `${engagement.plan_name} v${engagement.plan_version}` : '—'} />
            <Row k="Planned hours / cycle" v={engagement.planned_hours_per_cycle ?? '—'} />
            <Row k="Assigned to" v={engagement.assigned_to_name ?? 'unassigned'} />
            <Row k="Next cycle" v={engagement.next_cycle_date ? formatDate(engagement.next_cycle_date) : '—'} />
          </dl>
        </CardContent>
      </Card>

      <ClientProfileCard
        engagementId={engagement.id}
        serviceLineCode={engagement.service_line_code}
        profile={engagement.profile}
      />

      <Card className="lg:col-span-2">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Cycles</CardTitle></CardHeader>
        <CardContent className="p-0">
          {cycles.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-muted-foreground">
              No cycles yet. Generate one to create this period's work from the plan.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead className="text-right">Planned</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cycles.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.label}</TableCell>
                    <TableCell><Badge variant="secondary">{c.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress
                          value={c.work_item_count ? (c.completed_count / c.work_item_count) * 100 : 0}
                          className="h-1.5 w-28"
                        />
                        <span className="text-xs text-muted-foreground">
                          {c.completed_count}/{c.work_item_count}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{Number(c.planned_hours).toFixed(2)}h</TableCell>
                    <TableCell className="text-right tabular-nums">{Number(c.actual_hours).toFixed(2)}h</TableCell>
                    <TableCell><CycleStatusControl cycle={c} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{k}</dt>
      <dd>{v}</dd>
    </>
  );
}

// ------------------------------------------------------------- tab 2

function PlanTab({ planId, plannedHours }: { planId: string | null; plannedHours?: number }) {
  const { data } = useQuery({
    queryKey: ['svc', 'plan', planId],
    queryFn: () => api.getServicePlan(planId!),
    enabled: !!planId,
  });

  if (!planId) {
    return (
      <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
        No plan attached. Without one, generating a cycle produces no work.
      </CardContent></Card>
    );
  }

  const items = data?.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          {data?.data?.name}
          <span className="ml-2 font-normal text-xs text-muted-foreground">
            {plannedHours ?? 0}h planned per cycle
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Deliverable</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead className="text-right">Est. hours</TableHead>
              <TableHead>Performed by</TableHead>
              <TableHead>Gates</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id}>
                <TableCell className="font-medium">{i.name}</TableCell>
                <TableCell className="text-sm capitalize">{i.frequency.replace('_', ' ')}</TableCell>
                <TableCell className="text-right tabular-nums">{i.estimated_hours}</TableCell>
                <TableCell className="text-sm capitalize">{i.responsible_role}</TableCell>
                <TableCell className="space-x-1">
                  {!i.is_included && <Badge variant="outline">out of scope</Badge>}
                  {i.requires_internal_review && <Badge variant="secondary">review</Badge>}
                  {i.requires_client_approval && <Badge variant="secondary">client</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="px-6 py-3 text-xs text-muted-foreground border-t">
          Estimated hours are a planning figure used to size the cycle, not a target to hit.
        </p>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------- tab 3

function WorkTab({ engagementId }: { engagementId: string }) {
  const { data } = useQuery({
    queryKey: ['svc', 'work-items', engagementId],
    queryFn: () => api.getWorkItems({ engagementId }),
  });
  const rows = (data?.data ?? []) as WorkItem[];

  return (
    <Card>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-muted-foreground">
            No work yet for this client.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task</TableHead>
                <TableHead>Cycle</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Due</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((w) => (
                <TableRow key={w.id}>
                  <TableCell className="font-medium">{w.title}</TableCell>
                  <TableCell className="text-sm">{w.cycle_label}</TableCell>
                  <TableCell className="text-sm">{w.assignee_name ?? '—'}</TableCell>
                  <TableCell className="text-sm">
                    {w.due_date ? formatDate(w.due_date) : '—'}
                    {w.is_overdue && <Badge variant="destructive" className="ml-2">late</Badge>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {Number(w.actual_hours).toFixed(2)}
                    {w.estimated_hours != null && (
                      <span className="text-muted-foreground"> / {w.estimated_hours}</span>
                    )}
                  </TableCell>
                  <TableCell><Badge variant="secondary">{w.status.replace('_', ' ')}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------- tab 4

function PerformanceTab({ engagementId }: { engagementId: string }) {
  const { data } = useQuery({
    queryKey: ['svc', 'metrics', engagementId],
    queryFn: () => api.getEngagementMetrics(engagementId),
  });
  const rows = (data?.data ?? []) as MetricSnapshot[];

  // Grouped by source on purpose. Search Console clicks, GA4 sessions and a
  // rank tracker's position measure different things; charting them on one axis
  // produces a number that is wrong but still looks like a number.
  const bySource = rows.reduce<Record<string, MetricSnapshot[]>>((acc, m) => {
    (acc[m.source] ??= []).push(m);
    return acc;
  }, {});

  const SOURCE_LABEL: Record<string, string> = {
    search_console: 'Google Search Console',
    ga4: 'Google Analytics 4',
    gbp: 'Google Business Profile',
    rank_tracker: 'Rank tracker',
    manual: 'Entered manually',
  };

  if (rows.length === 0) {
    return (
      <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
        No performance data recorded yet. Metrics are stored per source and never combined.
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(bySource).map(([source, metrics]) => (
        <Card key={source}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{SOURCE_LABEL[source] ?? source}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metric</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="text-sm">
                      {m.metric_key}
                      {m.dimension && <span className="text-muted-foreground"> · {m.dimension}</span>}
                    </TableCell>
                    <TableCell className="text-sm">{m.period_start}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {m.value ?? '—'}{m.unit ? ` ${m.unit}` : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ------------------------------------------------------------- tab 5

function BillingTab({ cycle }: { cycle?: Cycle }) {
  const { data, isError } = useQuery({
    queryKey: ['svc', 'cycle-financials', cycle?.id],
    queryFn: () => api.getCycleFinancials(cycle!.id),
    enabled: !!cycle?.id,
    retry: false,
  });

  if (!cycle) {
    return (
      <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
        No cycle yet.
      </CardContent></Card>
    );
  }

  const f = data?.data as CycleFinancials | undefined;

  // The endpoint is manager-gated. Anyone below that gets no figures rather
  // than figures with the money blanked out client-side.
  if (isError || (data && !data.success)) {
    return (
      <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
        Financial detail is available to managers.
      </CardContent></Card>
    );
  }

  const unpriced = Number(f?.unpriced_minutes ?? 0);

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{cycle.label}</CardTitle></CardHeader>
      <CardContent>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Fig label="Contracted fee" value={f?.contracted_fee != null ? formatCurrency(f.contracted_fee) : '—'} />
          <Fig
            label="Direct labour cost"
            value={formatCurrency(f?.labor_cost ?? 0)}
            hint={unpriced > 0 ? 'Incomplete' : undefined}
            muted={unpriced > 0}
          />
          {/* Withheld rather than shown overstated — see the note below. */}
          <Fig
            label="Gross contribution"
            value={unpriced > 0 ? '—' : formatCurrency(f?.gross_contribution ?? 0)}
            hint={unpriced > 0 ? 'Not calculable yet' : 'Before overhead'}
            muted={unpriced > 0}
          />
          <Fig
            label="Revenue per delivery hour"
            value={f?.revenue_per_delivery_hour != null ? formatCurrency(f.revenue_per_delivery_hour) : '—'}
          />
          <Fig label="Planned hours" value={`${Number(f?.planned_hours ?? 0).toFixed(2)}h`} />
          <Fig label="Actual hours" value={`${Number(f?.actual_hours ?? 0).toFixed(2)}h`} />
          <Fig label="Included in subscription" value={`${Number(f?.included_hours ?? 0).toFixed(2)}h`} />
          <Fig label="Separately billable" value={`${Number(f?.billable_hours ?? 0).toFixed(2)}h`} />
        </div>

        <div className="mt-4 border-t pt-3 space-y-1.5">
          {unpriced > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-500">
              <strong>{(unpriced / 60).toFixed(2)} delivery hours have no labour rate</strong>, so
              they cost nothing in the figures above. Set a rate for whoever logged them under
              Time Tracking → Rates, then the contribution will calculate.
            </p>
          )}
          {f?.cost_basis_estimated && (
            <p className="text-xs text-amber-700 dark:text-amber-500">
              Some hours were priced from wage × (1 + burden) rather than a loaded cost rate,
              so this figure is an estimate.
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Gross contribution is the fee minus <strong>direct labour only</strong>. It is not profit.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function Fig({ label, value, hint, muted }: {
  label: string; value: string; hint?: string; muted?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-semibold mt-0.5 tabular-nums ${muted ? 'text-muted-foreground' : ''}`}>
        {value}
      </p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
